import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { FunctionComponent } from 'react';
import { DefaultEventComponent, EventComponentProps } from '../EventComponent';
import { CalendarEvent } from '..';
import { DateRange, isDateInRange } from '../../../services/DateRange';
import styles from './styles.module.scss';
import classNames from 'classnames';
import { useActiveRange } from '../../../hooks/useActiveRange';
import { CalendarContext } from '../../../contexts';
import { useTimezone } from '../../../hooks';

export type EmptyFallbackProps = {
    range: DateRange;
};

export type EventListProps<T> = {
    events: Array<CalendarEvent<T>>;
    precision?: 'day' | 'month';
    EventComponent?: FunctionComponent<EventComponentProps<T>>;
    EmptyFallback?: FunctionComponent<EmptyFallbackProps>;
    onVisibleRangeChanged?: (range: DateRange) => void;
    className?: string;
};

export const EventList = <T,>({
    events,
    precision = 'month',
    EventComponent = DefaultEventComponent,
    EmptyFallback = () => null,
    onVisibleRangeChanged = () => {},
    className,
}: EventListProps<T>) => {
    const { locale, timezone } = useContext(CalendarContext);
    const { parseDate } = useTimezone(timezone);
    const range = useActiveRange(precision);

    useEffect(() => {
        onVisibleRangeChanged(range);
    }, [onVisibleRangeChanged, range]);

    const eventsInRange = useMemo(() => {
        return events.filter((event) => isDateInRange(event.range.from, range));
    }, [events, range]);

    const doesEventSpanMultipleDays = useCallback(
        (event: CalendarEvent<T>): boolean => {
            const { day: startingDay } = parseDate(event.range.from);
            const { day: endingDay } = parseDate(event.range.to);
            return startingDay !== endingDay;
        },
        [parseDate]
    );

    if (eventsInRange.length === 0) {
        return <EmptyFallback range={range} />;
    }

    return (
        <div className={classNames(styles.eventList, className)}>
            <ol>
                {eventsInRange.map((event, index) => {
                    return (
                        <li key={index}>
                            <EventComponent
                                event={event}
                                locale={locale}
                                timezone={timezone}
                                spansMultipleDays={doesEventSpanMultipleDays(event)}
                            />
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};
